import { environment } from '@environment';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';

import { UtilityService } from '../util/utility.service';
import { LoginService } from '../login/login.service';
import { GtmService } from '@core/services/analytics/gtm.service';
import { CartService } from '@core/services/cart/cart.service';
import { DeviceService } from '@core/services/util/device.service';
import { StorageService } from '@core/services/util/storage.service';
import { OutletService } from '@core/services/outlet/outlet.service';
import { SessionStorageService } from '../util/session-storage.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { AddressService } from '@core/services/address/address.service';
import { MultiBrandPassService } from '@core/services/multi-brand-pass/multi-brand-pass.service';
import { AuthenticationService } from '@app/modules/authentication/services/authentication.service';

import { AgentOfferComponent } from '@app/modules/onboarding/offer/agent-offer/agent-offer.component';
import { OutletSelectionComponent } from '@app/modules/onboarding/outlet-selection/outlet-selection.component';
import { AgentOfferIntroComponent } from '@app/modules/onboarding/offer/agent-offer-intro/agent-offer-intro.component';

import { MenuModel } from '@app/shared/models/menu-model';
import { ServiceType } from '@app/shared/enums/service-type.enum';
import { AgentOfferApiResponse } from '@app/shared/models/api-response/agent-offers-response';

type OfferParams = {
    agent_identifier?: string;
    outlet_id?: number;
    service_type?: string;
    wa_aid?: string;
    wa_os?: string;
};

@Injectable({
    providedIn: 'root'
})
export class OnboardingService {
    isMobile = false;
    outletData: any = {};
    menuModel: MenuModel;

    constructor(
        private http: HttpClient,
        private dialog: MatDialog,
        private gtmService: GtmService,
        private cartService: CartService,
        private bottomSheet: MatBottomSheet,
        private deviceService: DeviceService,
        private toastrService: ToastrService,
        private outletService: OutletService,
        private utilityService: UtilityService,
        private loginService: LoginService,
        private storageService: StorageService,
        private addressService: AddressService,
        private authenticationService: AuthenticationService,
        private sessionStorage: SessionStorageService,
        private multiBrandPassService: MultiBrandPassService
    ) {
        this.isMobile = this.deviceService.isMobile();
    }

    addPass() {
        const customerPassDetails = this.sessionStorage.get('passes');
        const passList: any = this.multiBrandPassService.createPassData(customerPassDetails.all_passes);
        if (passList.length) {
            this.cartService.addPass(passList[0]);
        } else {
            this.gtmService.logEvent('web_analytics', {
                event_action: 'new_ordering_flow',
                event_label: 'Pass Product Not Present',
                value1: 'Outlet id: ' + this.outletService.state.value.outlet.id
            });
        }
    }

    copyCode(code: string) {
        this.utilityService.copyCode(code);
    }

    getAgentBasedOffer(params: OfferParams) {
        const headers = this.utilityService.getAuthHeaders();
        const offer_url = '/third_party/agent_offer/get_offer';

        return this.http.get(environment.c3pServiceUrl + offer_url, {
            params,
            headers
        });
    }

    private _isLoggedIn(): boolean {
        return Boolean(this.storageService.get('session-id') && this.storageService.get('phone_no'));
    }

    private _showOutletSelectionDialog() {
        const data = {
            disableClose: true,
            panelClass: 'address-selection-dialog',
            data: {
                outlet: this.outletService.state.value
            }
        };

        this.dialog
            .open(OutletSelectionComponent, data)
            .afterClosed()
            .subscribe({
                next: data => {
                    if (data) {
                        const { type, agent_id, outlet_id, service_type } = data;
                        if (service_type) {
                            // set address type on confirm, since for the same outlet id there can be a case where both dine-in and delivery are there
                            // since the outlet id is the same, we won't call the catalog fetch API, but we need to update the service type in this case

                            let type_label: string;

                            switch (service_type) {
                                case ServiceType.DINEIN:
                                    type_label = 'DINE IN';
                                    this.storageService.set('address_id', '-2');
                                    break;
                                case ServiceType.DELIVERY:
                                    type_label = 'Delivery';
                                    break;
                                default:
                                    type_label = 'DELIVER AT';
                            }

                            this.storageService.set('service_type', service_type);
                            this.storageService.set('address_type', type_label);
                            this.addressService.setLocality();
                        }

                        this.storageService.set('outlet_selection_shown', 'true');

                        this.gtmService.logEvent('web_analytics', {
                            event_action: 'new_ordering_flow',
                            event_label: 'Outlet Selection Screen',
                            value1: 'Closed: ' + type
                        });

                        switch (type) {
                            case 'current-outlet': {
                                this.showOfferScreen({
                                    agent_identifier: agent_id,
                                    outlet_id,
                                    service_type
                                });
                                return;
                            }

                            case 'other-outlet': {
                                this.onOtherOutletSelection({ agent_id, outlet_id, service_type });
                                return;
                            }

                            default:
                                return;
                        }
                    }
                }
            });
    }

    onOtherOutletSelection(outletParams) {
        this.outletService.getOutletById(outletParams);
    }

    async showOfferIntroDialog(params: OfferParams) {
        await this.loginService.ensureSessionId(true);
        lastValueFrom(this.getAgentBasedOffer(params))
            .then((response: AgentOfferApiResponse) => {
                if (response?.data.show) {
                    const dialogRef = this.dialog.open(AgentOfferIntroComponent, {
                        disableClose: true,
                        data: {
                            response: response.data
                        },
                        panelClass: 'offer-intro-dialog'
                    });

                    const loginSuccess$ = this.loginService.userLoginSuccess.subscribe(() => {
                        this.agentBasedOffer(params);

                        if (loginSuccess$) {
                            loginSuccess$.unsubscribe();
                        }
                    });

                    dialogRef.afterClosed().subscribe(() => {
                        this.authenticationService.resetStep();
                        if (loginSuccess$) {
                            loginSuccess$.unsubscribe();
                        }
                    });
                }
            })
            .catch(error => {
                console.log(error);
                this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            });
    }

    async agentBasedOffer(params: OfferParams) {
        await this.loginService.ensureSessionId(true);
        lastValueFrom(this.getAgentBasedOffer(params))
            .then((response: AgentOfferApiResponse) => {
                if (response?.data.show) {
                    const { eligible_for_subscription } = response.data;

                    if (eligible_for_subscription) {
                        this.addPass();
                    }

                    const data = {
                        offer_details: response.data
                    };

                    if (this.isMobile) {
                        this.bottomSheet.open(AgentOfferComponent, {
                            data,
                            panelClass: 'repeat-customization-sheet'
                        });
                    } else {
                        this.dialog.open(AgentOfferComponent, {
                            data,
                            panelClass: 'address-selection-dialog'
                        });
                    }
                }
            })
            .catch(error => {
                console.log(error);
                this.toastrService.openSnackBar('Something went wrong. Please try again!', 'OKAY');
            });
    }

    navigateToOnboarding(data) {
        if (this.userComingFromWhatsappFlow()) {
            return;
        }

        // check for nearby outlets, if multiple outlets are present
        const { nearby, outlet, outside_link } = data;

        if ((nearby && nearby.length) || outside_link) {
            const { active, open, id: currentOutletId, preorder_available } = outlet;
            const isCurrentOutletServiceable = active && (open || preorder_available);

            const isOtherOutletServiceable =
                nearby.some((outlet: any) => outlet.serviceable) || (outside_link && outside_link.serviceable);
            if (isCurrentOutletServiceable) {
                this._showOutletSelectionDialog();
            } else if (isOtherOutletServiceable) {
                this.gtmService.logEvent('web_analytics', {
                    event_action: 'new_ordering_flow',
                    event_label: 'Current Outlet Not Serviceable',
                    value1: 'Outlet id: ' + currentOutletId
                });
                this._showOutletSelectionDialog();
            } else {
                const outletIds = [currentOutletId, ...nearby.map((outlet: any) => outlet.outlet_id)];
                if (outside_link) {
                    outletIds.push(outside_link.outlet_id);
                }

                this.gtmService.logEvent('web_analytics', {
                    event_action: 'new_ordering_flow',
                    event_label: 'No Outlets Serviceable',
                    value1: 'Outlet id: ' + outletIds.join('-')
                });
            }
        } else {
            // If no nearby outlets or outside link, check if there's an agent_id and show an offer screen
            const { agent_id, id, service_type } = outlet;

            this.showOfferScreen({
                agent_identifier: agent_id,
                outlet_id: id,
                service_type
            });
        }
    }

    userComingFromWhatsappFlow() {
        const wa_aid = this.sessionStorage.get('wa_aid'); // whatsapp agent id
        const wa_os = this.sessionStorage.get('wa_os'); // user segment id for differentitating different user type[old, new, zomato, etc.]

        if (wa_aid && wa_os) {
            this.gtmService.logEvent('web_analytics', {
                event_action: 'new_ordering_flow',
                event_label: 'WhatsApp Flow'
            });

            this.showOfferScreen({
                wa_aid,
                wa_os
            });

            return true;
        }

        return false;
    }

    showOfferScreen(params: OfferParams) {
        this.storageService.set('outlet_selection_shown', 'true');

        const { agent_identifier, wa_aid, wa_os } = params;

        if (agent_identifier || (wa_aid && wa_os)) {
            const isLoggedIn = this._isLoggedIn();
            if (isLoggedIn) {
                this.agentBasedOffer(params);
            } else {
                this.showOfferIntroDialog(params);
            }
        }
    }
}
